/**
 * The parts of a PDF report that every transect-based analysis shares.
 * Shoreline, Shore-R, CSqueeze and EUSCoast all measure along transects and all carry the same
 * per-transect statistics, so summarising them belongs here rather than four times over - the same
 * reason {@link module:COASTLINE_GEOJSON} exists beside it. A module adds its own sections around
 * what these functions return.
 * Everything here is a pure function of data the result page already holds, and every piece of
 * wording arrives already translated, so a module's report stays testable without a browser.
 * @module COASTLINE_PDF_REPORT
 */

/**
 * @constant TRANSECT_TABLE_LIMIT
 * @description How many transects are listed one by one. An analysis can carry hundreds, and a
 * report that spends fifteen pages on a table nobody reads row by row is worse than one that
 * summarises and says it did. The summary above the table covers all of them; the listing is for
 * spot-checking, so the first ones are enough and the rest are counted in a note.
 * @memberof module:COASTLINE_PDF_REPORT
 */
export const TRANSECT_TABLE_LIMIT = 60;

/**
 * @constant RATE_FIELDS
 * @description The per-transect statistics, in the order they are reported. These are the names
 * {@link transectStatistics} writes, so a change there surfaces here as a missing column rather
 * than as a silently empty one.
 * @memberof module:COASTLINE_PDF_REPORT
 */
export const RATE_FIELDS = ['lrr', 'epr', 'nsm', 'sce', 'rsquared'];

/**
 * @function numbersOf
 * @description The finite values of one field across the transects, dropping anything that is not
 * a number. A transect that never met a coastline carries nulls, and averaging those in would
 * quietly pull every summary towards zero.
 * @param {Array<Object>} transects - Transects, either flat or with a `properties` object.
 * @param {String} field - The statistic to read.
 * @memberof module:COASTLINE_PDF_REPORT
 * @returns {Array<Number>}
 */
export const numbersOf = (transects, field) =>
  (transects || [])
    .map((transect) => Number((transect?.properties ?? transect ?? {})[field]))
    .filter((value) => Number.isFinite(value));

/**
 * @function summarise
 * @description Mean, smallest and largest of a list, or `null` when there is nothing to summarise.
 * @param {Array<Number>} values - Finite numbers.
 * @memberof module:COASTLINE_PDF_REPORT
 * @returns {Object|null} `{ mean, min, max, n }`.
 */
export const summarise = (values) => {
  if (!values?.length) return null;
  const total = values.reduce((sum, value) => sum + value, 0);
  return { mean: total / values.length, min: Math.min(...values), max: Math.max(...values), n: values.length };
};

/**
 * @function rateTable
 * @description The per-transect statistics summarised one row per statistic, which is how a reader
 * sees at a glance whether the coast is eroding on average and how much the transects disagree.
 * @param {Array<Object>} transects - The analysed transects.
 * @param {Object} text - `{ columns: { statistic, mean, min, max, count }, names: { lrr, ... } }`,
 *  already translated.
 * @memberof module:COASTLINE_PDF_REPORT
 * @returns {Object} A table for {@link renderReport}.
 */
export const rateTable = (transects, text) => ({
  columns: [text.columns.statistic, text.columns.mean, text.columns.min, text.columns.max, text.columns.count],
  rows: RATE_FIELDS.map((field) => {
    const stats = summarise(numbersOf(transects, field));
    return [
      text.names[field] ?? field,
      stats ? stats.mean.toFixed(3) : null,
      stats ? stats.min.toFixed(3) : null,
      stats ? stats.max.toFixed(3) : null,
      stats ? stats.n : 0,
    ];
  }).filter((row) => row[4] > 0),
});

/**
 * @function classificationTable
 * @description How many transects fell in each class, with the share of the total.
 * The share is what the classification is for: "forty transects eroded" means nothing without
 * knowing whether there were fifty or five hundred.
 * @param {Array<Object>} transects - The analysed transects.
 * @param {String} field - The property holding the class, e.g. `'label'`.
 * @param {Object} text - `{ columns: { className, count, share } }`, already translated.
 * @param {Function} [className] - Translates a stored class into what the reader sees.
 * @memberof module:COASTLINE_PDF_REPORT
 * @returns {Object} A table for {@link renderReport}.
 */
export const classificationTable = (transects, field, text, className = (value) => value) => {
  const counts = new Map();
  (transects || []).forEach((transect) => {
    const value = (transect?.properties ?? transect ?? {})[field];
    if (value === null || value === undefined || value === '') return;
    counts.set(value, (counts.get(value) || 0) + 1);
  });

  const total = [...counts.values()].reduce((sum, count) => sum + count, 0);
  return {
    columns: [text.columns.className, text.columns.count, text.columns.share],
    rows: [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => [className(value), count, total ? `${((count / total) * 100).toFixed(1)}%` : null]),
  };
};

/**
 * @function transectTable
 * @description The transects listed one by one, capped at {@link TRANSECT_TABLE_LIMIT}.
 * @param {Array<Object>} transects - The analysed transects.
 * @param {Object} text - `{ columns: { id, lrr, epr, nsm, sce, rsquared, className } }`, already
 *  translated.
 * @param {Function} [className] - Translates a stored class into what the reader sees.
 * @memberof module:COASTLINE_PDF_REPORT
 * @returns {Object} A table for {@link renderReport}.
 */
export const transectTable = (transects, text, className = (value) => value) => {
  const metre = (value) =>
    Number.isFinite(Number(value)) && value !== null && value !== '' ? Number(value).toFixed(2) : null;

  return {
    columns: [
      text.columns.id,
      text.columns.lrr,
      text.columns.epr,
      text.columns.nsm,
      text.columns.sce,
      text.columns.rsquared,
      text.columns.className,
    ],
    rows: (transects || []).slice(0, TRANSECT_TABLE_LIMIT).map((transect, index) => {
      const properties = transect?.properties ?? transect ?? {};
      return [
        properties.id ?? transect?.id ?? index + 1,
        metre(properties.lrr),
        metre(properties.epr),
        metre(properties.nsm),
        metre(properties.sce),
        metre(properties.rsquared),
        properties.label ? className(properties.label) : null,
      ];
    }),
  };
};

/**
 * @function transectTableNote
 * @description The line under the per-transect table, saying how many were listed out of how many.
 * Written whether or not the table was capped: a reader has to know the table is complete just as
 * much as they have to know it is not.
 * @param {Array<Object>} transects - The analysed transects.
 * @param {Object} text - `{ complete, truncated }`, translated, taking `{{shown}}` and `{{total}}`.
 * @memberof module:COASTLINE_PDF_REPORT
 * @returns {String|null}
 */
export const transectTableNote = (transects, text) => {
  const total = transects?.length || 0;
  if (!total) return null;
  const shown = Math.min(total, TRANSECT_TABLE_LIMIT);
  return shown < total ? text.truncated({ shown, total }) : text.complete({ total });
};

/**
 * @function periodOf
 * @description The span the analysis covers, from the dates the transects recorded.
 * Read from the transects rather than from the image chooser because this is the period that was
 * actually measured: an image that no transect intersected contributes no date here.
 * @param {Array<Object>} transects - The analysed transects.
 * @memberof module:COASTLINE_PDF_REPORT
 * @returns {String|null} `'first → last'`, or `null` when no transect carries dates.
 */
export const periodOf = (transects) => {
  const dates = (transects || [])
    .flatMap((transect) => {
      const properties = transect?.properties ?? transect ?? {};
      return [properties.firstDate, properties.lastDate];
    })
    .filter(Boolean)
    .map((value) => String(value).slice(0, 10))
    .sort();
  if (!dates.length) return null;
  return `${dates[0]} → ${dates[dates.length - 1]}`;
};
